import type { InstagramCreator } from '@frontend/gql/social/graphql';
import type {
  DemographicsDataAdapter,
  DemographicsData,
  DemographicsSummary,
  DemographicsDisplayConfig
} from '@/types/demographics';
import { getCountryLabel } from '../../components/filters-form/utils';
import { AGE_RANGE_DISPLAY_MAP } from '../types/instagram-creator-age-bucket';

// Instagram-specific display configuration
const INSTAGRAM_DISPLAY_CONFIG: DemographicsDisplayConfig = {
  ageBuckets: {
    AGE_18_TO_24: { displayName: '18-24', color: '#2F7772' },
    AGE_25_TO_34: { displayName: '25-34', color: '#5A9692' },
    AGE_35_TO_44: { displayName: '35-44', color: '#5DB8B3' },
    AGE_45_TO_54: { displayName: '45-54', color: '#B2E5E2' },
    AGE_55_TO_64: { displayName: '55-64', color: '#D0F6F3' },
    AGE_65_AND_ABOVE: { displayName: '65+', color: '#16282D' },
    UNKNOWN: { displayName: 'Unknown', color: '#CCCCCC' },
  },
  genders: {
    MALE: { displayName: 'Male', color: '#2F7772' },
    FEMALE: { displayName: 'Female', color: '#5A9692' },
    CUSTOM: { displayName: 'Custom', color: '#5DB8B3' },
    UNKNOWN: { displayName: 'Unknown', color: '#CCCCCC' },
  },
  unknownValues: {
    ageBucket: { displayName: 'Unknown', color: '#CCCCCC' },
    gender: { displayName: 'Unknown', color: '#CCCCCC' },
  },
};

export class InstagramDemographicsAdapter implements DemographicsDataAdapter<InstagramCreator> {
  extractDemographicsData(creator: InstagramCreator): DemographicsData | null {
    const audience = creator?.insights?.audience;
    if (!audience) return null;

    return {
      ageDistribution: audience.ageDistribution?.map((entry) => ({
        ageBucket: entry.ageBucket || 'UNKNOWN',
        percentage: entry.percentage || 0,
      })),
      genderDistribution: audience.genderDistribution?.map((entry) => ({
        gender: entry.gender || 'UNKNOWN',
        percentage: entry.percentage || 0,
      })),
      topCountries: audience.topCountries?.map((entry) => ({
        name: entry.country || '',
        percentage: entry.percentage || 0,
      })),
      topCities: audience.topCities?.map((entry) => ({
        name: entry.city || '',
        percentage: entry.percentage || 0,
      })),
    };
  }

  extractSummary(creator: InstagramCreator): DemographicsSummary {
    try {
      const audience = creator?.insights?.audience;
      if (!audience) {
        return { ageBucket: '--', gender: '--', country: '--', city: '--' };
      }

      const { ageDistribution = [], genderDistribution = [], topCountries = [], topCities = [] } = audience;

      const topAgeBucket = ageDistribution?.reduce((max, current) => {
        return current.percentage > (max?.percentage ?? 0) ? current : max;
      });

      const rawAgeBucket = topAgeBucket?.ageBucket;
      const ageBucket = rawAgeBucket ? AGE_RANGE_DISPLAY_MAP[rawAgeBucket] || '--' : '--';

      const topGender = genderDistribution?.reduce((max, current) => {
        return current.percentage > (max?.percentage ?? 0) ? current : max;
      });

      const rawGender = topGender?.gender;
      const gender = rawGender ? rawGender.toLowerCase() : '--';

      const topCountry = topCountries?.reduce((max, current) => {
        return current.percentage > (max?.percentage ?? 0) ? current : max;
      });
      const country = topCountry?.country ? getCountryLabel(topCountry?.country) : '--';

      const topCity = topCities?.reduce((max, current) => {
        return current.percentage > (max?.percentage ?? 0) ? current : max;
      });
      const city = topCity?.city || '--';

      return { ageBucket, gender, country, city };
    } catch (error) {
      console.error('Error processing basic demographics:', error);
      return { ageBucket: '--', gender: '--', country: '--', city: '--' };
    }
  }

  getDisplayConfig(): DemographicsDisplayConfig {
    return INSTAGRAM_DISPLAY_CONFIG;
  }
}
